import { getCordClientAuthToken } from "frontend/api";
import { useEffect, useMemo, useState } from "react";
import { useCordThreadActivitySummary } from "@cord-sdk/react";
import { CordThread, CordUserMap } from "shared/datamodel/cord";

export const cordEnabled = () => process.env.CORD_ENABLED == "true";

export const NoCordMsg =
  "Comments are allowed for members of this account only. Contact the account's admin to add you to this account";

export function useCord({ documentId, isLoggedIn }: { documentId?: string; isLoggedIn: boolean }) {
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    if (!cordEnabled()) return;
    if (!isLoggedIn) return;
    if (!documentId) return;
    getCordClientAuthToken(documentId)
      .then(setToken)
      .catch((error) => console.error("Error getting cord token", error));
  }, [isLoggedIn]);

  return token;
}

export const useCordThreads = (documentId?: string): CordThread[] => {
  const [allThreads, setThreads] = useState<CordThread[]>([]);
  const threadActivitySummary = useCordThreadActivitySummary({ documentId: documentId ?? "" });

  useEffect(() => {
    if (!documentId || !window.CordSDK) return;
    // @ts-expect-error cord sdk types
    const ref = window.CordSDK?.thread.observeThreads(
      ({
        threads,
        loading,
        hasMore,
        fetchMore,
      }: {
        threads: CordThread[];
        loading: boolean;
        hasMore: boolean;
        fetchMore: (count: number) => void;
      }) => {
        // Remove duplicate threads based on thread id before setting state
        // Merge allThreads and threads, then deduplicate by thread.id
        const mergedThreads = [...allThreads, ...threads];
        const uniqueThreads = [...new Map(mergedThreads.map((thread) => [thread.id, thread])).values()];
        setThreads(uniqueThreads);
        if (!loading && hasMore && threads.length < 25) {
          // Get the first 25 threads, 10 at a time.
          fetchMore(10);
        }
      },
      {
        filter: {
          location: {
            documentId,
          },
        },
      }
    );
    // ... Later, when updates are no longer needed ...
    return () => {
      // @ts-expect-error cord sdk types
      window.CordSDK?.thread.unobserveThreads(ref);
    };
  }, [documentId, threadActivitySummary]);

  return allThreads;
};

export const useCordUsers = (documentId?: string): CordUserMap => {
  const [users, setUsers] = useState<CordUserMap>(new Map());
  const threadActivitySummary = useCordThreadActivitySummary({ documentId: documentId ?? "" });

  useEffect(() => {
    if (!documentId) return;

    // @ts-expect-error cord sdk types
    setUsers(window?.CordSDK?.users._users ?? new Map());
  }, [documentId, threadActivitySummary]);
  return users;
};

export const useSearchCordThreads = (threads: CordThread[], search: string, documentId?: string) => {
  const normalizedSearch = search.trim().toLowerCase();
  const threadActivitySummary = useCordThreadActivitySummary({ documentId: documentId ?? "" });
  const [matchingThreadIds, setMatchingThreadIds] = useState<Set<string>>(new Set());
  const [lastSearch, setLastSearch] = useState("");

  useEffect(() => {
    if (!normalizedSearch || !window.CordSDK) {
      setMatchingThreadIds(new Set());
      setLastSearch("");
      return;
    }

    let cancelled = false;

    const searchThreadMessages = async () => {
      try {
        // @ts-expect-error cord sdk types
        const results = await window.CordSDK?.thread.searchMessages({
          plainText: normalizedSearch, // not working, need to manual filter
          locationOptions: {
            location: {
              documentId: documentId ?? "",
            },
            partialMatch: false,
          },
          limit: 1000,
        });

        if (cancelled) return;

        const threadIds = new Set<string>();
        if (Array.isArray(results)) {
          for (const message of results) {
            if (
              message?.threadID &&
              typeof message?.plaintext === "string" &&
              message.plaintext.toLowerCase().includes(normalizedSearch)
            ) {
              threadIds.add(message.threadID);
            }
          }
        }

        setMatchingThreadIds(threadIds);
        setLastSearch(normalizedSearch);
      } catch (error) {
        console.error("Error searching Cord messages", error);
        if (!cancelled) {
          setMatchingThreadIds(new Set());
          setLastSearch(normalizedSearch);
        }
      }
    };

    searchThreadMessages();

    return () => {
      cancelled = true;
    };
  }, [normalizedSearch, documentId, threadActivitySummary]);

  return useMemo(() => {
    // Use lastSearch (synced with matchingThreadIds) not normalizedSearch
    if (!lastSearch) {
      return threads;
    }

    return threads.filter((thread) => {
      // Search in participant names using lastSearch
      if (thread.participants?.some((participant) => participant.displayName?.toLowerCase().includes(lastSearch))) {
        return true;
      }

      // Check if any message in this thread matched the search
      return matchingThreadIds.has(thread.id);
    });
  }, [threads, lastSearch, matchingThreadIds]);
};

export const useCordData = (documentId?: string): { threads: CordThread[]; users: CordUserMap } => {
  const threads = useCordThreads(documentId);
  const users = useCordUsers(documentId);
  return { threads, users };
};
